import { IFormControl, IOption } from "@/types"
import { useMemo } from "react"
import { Controller, FieldValues } from "react-hook-form"
import countries from "react-intl-tel-input-18/dist/components/AllCountries"
import { Autocomplete } from "../Selector/Autocomplete"

import { useMediaQuery } from "@/hooks/useMediaQuery"
import { useIntl } from "react-intl"
import "./style.sass"
interface ICountryMeta {
  iso2: string
  dialCode: string
  priority: number
}
type CountryPhone = IOption<string, ICountryMeta>
interface ICountrySelectProps {
  label?: string
  dark?: boolean
  defaultCountry?: string
  firstCountries?: string[]
  error?: string
  value?: string
  onChange?: (country: CountryPhone) => void
  withDialCode?: boolean
  onlyFlag?: boolean
  searchable?: boolean
}
export const CountrySelect = ({
  withDialCode = false,
  error,
  firstCountries = ["gb"],
  defaultCountry = "gb",
  label,
  value,
  onChange,
  onlyFlag,
  searchable,
}: ICountrySelectProps) => {
  const isMobile = useMediaQuery("(max-width: 768px)")
  const allCountries = useMemo(() => {
    const result = countries.getCountries().reduce<Record<string, CountryPhone>>((acc, country) => {
      Object.assign(acc, {
        [country.iso2 ?? ""]: {
          label: withDialCode ? `${country.name} +${country.dialCode}` : country.name,
          value: `${country.iso2}`,
          meta: {
            iso2: country.iso2,
            dialCode: country.dialCode,
            priority: country.priority ?? 0,
          },
        },
      })
      return acc
    }, {})
    const firsts = firstCountries?.map((first) => result[first.toLowerCase()])
    firstCountries?.forEach((first) => delete result[first])
    return [...(firsts ?? []), ...Object.values(result)]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstCountries])
  const selectedCountry = useMemo(() => {
    return allCountries.find((country) => country.value === (value || defaultCountry))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, defaultCountry])

  return (
    <>
      <Autocomplete
        value={selectedCountry}
        options={allCountries}
        size="md"
        label={label}
        onChange={(value) => {
          onChange?.(value as IOption<string, ICountryMeta>)
        }}
        styles={
          onlyFlag
            ? {
                singleValue: (base) => ({ ...base, "& span": { display: "none" } }),
                container: (base) => ({ ...base, minWidth: 70, maxWidth: 70 }),
                menu: (base) => ({ ...base, width: `${isMobile ? window.screen.width * 0.8 : 480}px !important` }),
              }
            : {}
        }
        error={error}
        renderOption={(option) => (
          <div className="flex items-center gap-2 text-sm">
            <div className={`iti-flag ${option?.value} min-w-[fit-content]`} />
            <span className="text-sm">{option?.label}</span>
          </div>
        )}
        searchable={searchable}
      />
      {/* {error != null && <span className="text-red-500 text-[14px]">{error}</span>} */}
    </>
  )
}

type IFormCountrySelectProps<T extends FieldValues> = IFormControl<T> & ICountrySelectProps
export const FormCountrySelect = <T extends FieldValues>({
  control,
  name,
  rules,
  required,
  onlyValue,
  ...props
}: IFormCountrySelectProps<T> & { onlyValue?: boolean }) => {
  const { $t: t } = useIntl()
  return (
    <Controller
      {...{ control, name, rules: { ...rules, required: { value: required ?? false, message: t({ id: "required" }) } } }}
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <CountrySelect
          defaultCountry=""
          {...props}
          value={onlyValue ? value : (value as IOption<string, ICountryMeta>)?.value}
          searchable
          onChange={(value) => {
            if (onlyValue) {
              onChange(value?.value)
            } else {
              onChange(value)
            }
          }}
          error={error?.message}
        />
      )}
    />
  )
}
